import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Wand2,
  Video,
  Film,
  Sparkles,
  Layers,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Building2,
  Tv,
  Monitor,
  Share2,
  PieChart,
  Eye,
  TrendingUp,
  Bot,
  Mic,
  Languages,
  Scissors,
  Image as ImageIcon,
  Repeat,
  Globe,
} from "lucide-react";

export const Route = createFileRoute("/services_/ai-video-creation")({
  head: () => ({
    meta: [
      {
        title:
          "AI Video Creation Services (Long-form & Short-form) — KMWAdvertising LLP",
      },
      {
        name: "description",
        content:
          "Scale Content Production with AI-Powered Video Creation by KMWAdvertising LLP. Long-form corporate films, Reels, YouTube Shorts, AI avatars, voiceovers, and automated dubbing.",
      },
      {
        property: "og:title",
        content: "AI Video Creation Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Produce personalized, high-performance video content at scale with AI scripting, avatars, voiceovers, multi-language dubbing, and automated editing.",
      },
    ],
  }),
  component: AIVideoCreationServicePage,
});

export function AIVideoCreationServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const longFormVideos = [
    "Corporate Films",
    "Brand Story Videos",
    "Product Demonstrations",
    "Educational Videos",
    "Company Profile Videos",
    "Customer Success Stories",
    "Case Study Videos",
    "Webinar Videos",
    "Training & E-learning Content",
    "Investor Presentations",
    "Documentary-style Brand Films",
  ];

  const shortFormVideos = [
    "Instagram Reels",
    "YouTube Shorts",
    "LinkedIn Video Content",
    "Facebook Video Ads",
    "Product Promotions",
    "Festival Campaigns",
    "Event Highlights",
    "Motion Posters",
    "Promotional Teasers",
    "Testimonial Videos",
    "Explainer Snippets",
    "AI Avatar Videos",
    "AI Voice-over Videos",
    "Captioned Social Videos",
  ];

  const aiCapabilities = [
    "AI Script Assistance",
    "AI Voice Generation",
    "AI Avatars",
    "Automated Subtitle Generation",
    "Multi-language Dubbing",
    "AI Video Editing",
    "Background Removal",
    "Scene Enhancement",
    "AI Image Generation",
    "AI Motion Graphics",
    "Content Repurposing",
    "Video Localisation",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">AI Video Creation</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Wand2 className="h-3.5 w-3.5" />
              AI Video Creation (Long-form & Short-form)
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              AI Video Creation
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Scale Content Production with AI-Powered Video Creation
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                Artificial Intelligence is transforming content production by enabling brands to create high-quality videos faster, more efficiently and at scale. AI-powered video creation allows businesses to produce personalised marketing content without compromising creativity or quality.
              </p>
              <p className="text-sm sm:text-base text-slate-400">
                At KMWAdvertising LLP, we combine AI technology with creative direction, storytelling and human expertise to deliver videos that feel authentic, engaging and brand-consistent.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                Rather than replacing creativity, AI enhances production by accelerating scripting, editing, voice generation, subtitles, localisation and content adaptation across multiple platforms.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#ai-video-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Start AI Video Project</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#ai-capabilities"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore AI Capabilities</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Long-Form AI Videos */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <Film className="h-3.5 w-3.5" />
              Authority & Trust Content
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Long-Form AI Videos
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Long-form videos help businesses educate audiences, establish authority and build trust. We create:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {longFormVideos.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-purple-500/30 transition flex items-center gap-3"
              >
                <Check className="h-4 w-4 text-purple-400 shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-white">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-purple-300/90 italic font-semibold">
            Long-form videos are ideal for websites, YouTube, presentations, trade shows and corporate communications.
          </p>
        </div>
      </section>

      {/* Short-Form AI Videos */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Video className="h-3.5 w-3.5" />
              High-Engagement Short Form
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Short-Form AI Videos
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Short-form content dominates today's digital landscape. Platforms like Instagram Reels, YouTube Shorts, Facebook, LinkedIn and TikTok reward engaging, concise video formats that deliver immediate value. Our short-form AI video services include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
            {shortFormVideos.map((item) => (
              <div
                key={item}
                className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition text-center text-xs font-semibold text-white"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-pink-300/90 italic font-semibold">
            Every video is optimised for platform-specific dimensions, audience behaviour and algorithm preferences to maximise reach and engagement.
          </p>
        </div>
      </section>

      {/* AI Video Capabilities */}
      <section id="ai-capabilities" className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-mono font-bold uppercase">
              <Wand2 className="h-3.5 w-3.5" />
              Advanced Production Tech
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              AI Video Capabilities
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Our AI-powered production workflow includes:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-10">
            {aiCapabilities.map((cap) => (
              <div
                key={cap}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-rose-500/40 transition"
              >
                {cap}
              </div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-rose-950/20 to-purple-950/30 border border-white/15">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-medium">
              By combining AI with creative expertise, we help brands produce more content in less time while maintaining professional quality.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="ai-video-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                AI Video Production Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Accelerate Your Video Production with AI
              </h2>
              <p className="text-slate-300 text-sm">
                Discuss your long-form or short-form video goals with our AI production team for a custom content scaling plan.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">AI Video Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our video production lead will reach out within 24 hours to schedule your strategy session.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Sanya Malhotra"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Nexus Global"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="sanya@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Video Format Focus</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="both">Both Long-Form & Short-Form Video Scaling</option>
                    <option value="short">Short-Form (Reels, Shorts, Social Ads)</option>
                    <option value="long">Long-Form (Corporate Films, Product Demos)</option>
                    <option value="avatars">AI Avatars & Voice-Over Multilingual Dubbing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Project Vision / Script Notes</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about the target audience, channels, or video volume goals..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request AI Video Production Brief</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
